import React, { useRef, useState } from 'react';
import PratusInput from '../../../../DesignSystems/New/PratusInput';
import StylishNewComboBox, {
  type ComboBoxOption,
} from '../../../../DesignSystems/New/StylishNewComboBox';
import { STRATEGIES_LIBRARY } from '../ostLibraryData';
import { StylishNewButton } from '../../../../DesignSystems/New/StylishNewButton';
import { SharedIcon } from '../../../../SharedIcon/SharedIcon';
import classNames from 'classnames';
import {
  IncidentObjective,
  useEditIncidentObjective,
} from '../../../hooks/incidentObjectiveHooks';
import { useCreateIncidentStrategy } from '../../../hooks/incidentStrategyHooks';

type ObjectiveItemProps = {
  item: IncidentObjective;
  selectedTactics: string[];
  readOnly?: boolean;
};

export const ObjectiveItem: React.FC<
  React.PropsWithChildren<ObjectiveItemProps>
> = ({ item, children, selectedTactics, readOnly = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);

  const itemRef = useRef<HTMLDivElement | null>(null);
  const editObjective = useEditIncidentObjective();
  const createStrategy = useCreateIncidentStrategy();

  const isDisabled = Array.isArray(children) && children.length > 0;

  const isSelected =
    Array.isArray(children) &&
    (children as React.ReactElement[]).some(
      (child) =>
        Array.isArray(child.props.children) &&
        child.props.children.some(
          (grandchild: React.ReactElement) =>
            grandchild?.props?.item?.tactic_id &&
            selectedTactics.includes(grandchild.props.item.tactic_id)
        )
    );

  const handleEdit = (value: string) =>
    editObjective.mutate({ ...item, name: value });

  const handleDelete = () => {
    editObjective.mutate({
      ...item,
      archived: true,
    });
  };

  const handleCreateStrategy = (strategyName: string) => {
    createStrategy.mutate({
      name: strategyName,
      description: '',
      tactics: [],
      objectives: [item.objective_id],
      incident_id: item.incident_id,
    });
  };

  return (
    <div className="mb-2" ref={itemRef}>
      <div
        role="button"
        className={classNames('pratus-collapsible-item', {
          'align-items-start': editing,
          corner: expanded,
        })}
        style={{ paddingRight: '2.25rem' }}
        onClick={() => {
          if (!editing && !isSelected) {
            setExpanded(!expanded);
          }
        }}
      >
        <div className="d-flex align-items-center flex-grow-1 gap-1">
          <SharedIcon
            iconName={expanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
            size="sm"
            classes="text-muted"
          />
          {!editing ? (
            <>{item.name}</>
          ) : (
            <PratusInput
              defaultValue={item.name}
              showErrorContainer={false}
              className="me-2 flex-grow-1"
              onSave={(value: string) => {
                handleEdit(value);
                setEditing(false);
              }}
              onCancel={() => setEditing(false)}
            />
          )}
        </div>
        {!readOnly && (
          <button
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'transparent',
              boxShadow: 'none',
              border: 'none',
            }}
          >
            <StylishNewButton
              className="button--link"
              onClick={() => {
                setEditing(!editing);
              }}
            >
              <SharedIcon iconName="edit" color="light" />
            </StylishNewButton>
            <StylishNewButton
              className="button--link"
              onClick={() => {
                handleDelete();
              }}
              disabled={isDisabled}
            >
              <SharedIcon
                iconName="delete"
                color={isDisabled ? undefined : 'light'}
              />
            </StylishNewButton>
          </button>
        )}
      </div>
      {expanded && (
        <div
          className="pratus-collapsible-item__content"
          style={{ backgroundColor: '#222529' }}
        >
          <div className="d-flex align-items-center justify-content-between mb-2 gap-1">
            Strategy
            {!readOnly && (
              <StylishNewButton onClick={() => setAdding(!adding)}>
                <SharedIcon iconName="add" classes="me-1" size="sm" />
                Add Strategy
              </StylishNewButton>
            )}
          </div>
          {adding && !readOnly && (
            <div className="bordered-card d-flex align-items-center m-0 mt-2 mb-2 gap-2">
              <StylishNewComboBox
                classes="flex-grow-1"
                options={STRATEGIES_LIBRARY}
                placeholder="Select or create Strategy..."
                customOptionLabel="+ Create New"
                onChange={(newValue) => {
                  const option = newValue as ComboBoxOption | null;
                  if (option) {
                    handleCreateStrategy(option.label);
                    setAdding(false);
                  }
                }}
              />
              <StylishNewButton
                className="button--icon primary"
                onClick={() => setAdding(false)}
              >
                <SharedIcon iconName="close" />
              </StylishNewButton>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
};
